<template>
  <div class="form-input">
    <label>{{ label }}</label>

    <div class="input-group">
      <input
        :id="name"
        ref="inputRef"
        type="text"
        :name="name"
        :disabled="disabled"
        class="form-control"
        autocomplete="off"
        @blur="onBlur"
      />

      <label :for="name" class="input-icon">
        <BartoIcon icon="reservation-calendar" />
      </label>
    </div>

    <span v-show="errorMessage" class="text-danger">{{ errorMessage }}</span>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue';
import type { Prop } from 'vue';
import type { RuleExpression } from 'vee-validate';
import { useField } from 'vee-validate';
import flatpickr from 'flatpickr';
import { Slovak } from 'flatpickr/dist/l10n/sk.js';
import type { Instance } from 'flatpickr/dist/types/instance';
import 'flatpickr/dist/flatpickr.min.css';
import BartoIcon from '../BartoIcon.vue';

// What the user sees and types, and what the model/API holds.
const displayFormat = 'j.n.Y';
const valueFormat = 'Y-m-d';
// How far back the year dropdown reaches — the field is a birthdate.
const yearsOffered = 120;

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },

  name: {
    required: true,
    type: String,
  },

  label: {
    type: String,
    default: '',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  max: {
    type: String,
    default: '',
  },

  rules: {} as Prop<RuleExpression<unknown>>,
});

const emit = defineEmits({
  'update:modelValue': (value: string) => value,
});

const inputRef = ref<HTMLInputElement | null>(null);
let picker: Instance | null = null;
let yearSelect: HTMLSelectElement | null = null;

const { value, errorMessage, handleChange, handleBlur } = useField<string>(
  toRef(props, 'name'),
  props.rules,
  { initialValue: props.modelValue }
);

// flatpickr parses every string it is handed with `dateFormat`, so anything coming
// from or going to the model has to be converted explicitly.
const toValue = (date?: Date) => (date ? flatpickr.formatDate(date, valueFormat) : '');
const toDate = (date: string) => (date ? flatpickr.parseDate(date, valueFormat) : undefined);

// allowInput lets the user type, but flatpickr never parses what was typed on its
// own — the text has to be handed back in. Unparseable text clears the field, which
// lets the required/date_format rules report it.
const syncTypedValue = () => picker?.setDate(picker.input.value, true);

const onBlur = (event: Event) => {
  // Picking a day blurs the input before the click lands. Re-parsing here would
  // rebuild the day cells mid-click and swallow the selection, so while the
  // calendar is open onClose is what syncs.
  if (!picker?.isOpen) {
    syncTypedValue();
  }

  handleBlur(event);
};

// flatpickr only offers a year spinner, which is unusable for a birthdate — it is
// dozens of clicks away from the year you want. Swap it for a dropdown, next to the
// month one flatpickr already renders.
const buildYearDropdown = (instance: Instance) => {
  const header = instance.calendarContainer.querySelector('.flatpickr-current-month');

  if (!header) {
    return;
  }

  const lastYear = (instance.config.maxDate ?? new Date()).getFullYear();

  yearSelect = document.createElement('select');
  // Borrows flatpickr's own dropdown styling; only the width is overridden.
  yearSelect.className = 'flatpickr-monthDropdown-months flatpickr-yearDropdown-years';
  yearSelect.setAttribute('aria-label', 'Rok');

  for (let year = lastYear; year >= lastYear - yearsOffered; year--) {
    yearSelect.add(new Option(String(year), String(year)));
  }

  yearSelect.value = String(instance.currentYear);
  yearSelect.addEventListener('change', () => {
    instance.jumpToDate(new Date(Number(yearSelect?.value), instance.currentMonth, 1));
  });

  header.appendChild(yearSelect);
  instance.calendarContainer.classList.add('custom-travelagency-datepicker-year-dropdown');
};

onMounted(() => {
  if (!inputRef.value) {
    return;
  }

  picker = flatpickr(inputRef.value, {
    dateFormat: displayFormat,
    // The date has to stay freely typeable, not click-only.
    allowInput: true,
    // Without this flatpickr falls back to the native picker on touch devices.
    disableMobile: true,
    locale: Slovak,
    maxDate: toDate(props.max),
    defaultDate: toDate(value.value),
    onChange: (dates: Date[]) => {
      const selected = toValue(dates[0]);

      handleChange(selected);
      emit('update:modelValue', selected);
    },
    onClose: syncTypedValue,
    onReady: (_dates: Date[], _dateStr: string, instance: Instance) => buildYearDropdown(instance),
    // Keeps the dropdown honest when the year changes some other way — paging past
    // December, or typing a date straight into the input.
    onYearChange: () => {
      if (yearSelect && picker) {
        yearSelect.value = String(picker.currentYear);
      }
    },
  });

  picker.calendarContainer.classList.add('custom-travelagency-datepicker-overrides');
});

onBeforeUnmount(() => {
  picker?.destroy();
  picker = null;
  yearSelect = null;
});

// The parent can push a value in (customer data is autosaved and restored).
watch(
  () => props.modelValue,
  (newValue) => {
    if ((newValue ?? '') !== (value.value ?? '')) {
      handleChange(newValue ?? '');
    }
  }
);

watch(value, (newValue) => {
  if (!picker || toValue(picker.selectedDates[0]) === (newValue ?? '')) {
    return;
  }

  picker.setDate(toDate(newValue ?? '') ?? '', false);
});
</script>
